import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Announcement } from "@/api";
import noticeIcon from "@/assets/images/notice.svg";
import { sortAnnouncements } from "@/utils/announcementSort";

const ROTATE_INTERVAL_MS = 4000;
const TICKER_LINE_HEIGHT_PX = 20;
const TICKER_TRANSITION_MS = 700;
const TICKER_VISIBLE_COUNT = 3;

export type DashboardAnnouncementBarProps = {
  announcements: Announcement[];
};

export function DashboardAnnouncementBar({
  announcements,
}: DashboardAnnouncementBarProps) {
  const { t } = useTranslation();
  const items = useMemo(
    () => sortAnnouncements(announcements).slice(0, TICKER_VISIBLE_COUNT),
    [announcements]
  );
  const tickerItems = useMemo(
    () => (items.length > 1 ? [...items, items[0]] : items),
    [items]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);

  useEffect(() => {
    if (items.length <= 1) return;

    const intervalId = setInterval(() => {
      setIsTransitionEnabled(true);
      setActiveIndex((prev) => prev + 1);
    }, ROTATE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [items.length]);

  useEffect(() => {
    if (items.length <= 1 || activeIndex !== items.length) return;

    const timeoutId = setTimeout(() => {
      setIsTransitionEnabled(false);
      setActiveIndex(0);
    }, TICKER_TRANSITION_MS);

    return () => clearTimeout(timeoutId);
  }, [activeIndex, items.length]);

  if (items.length === 0) return null;

  return (
    <Link
      to="/announcements"
      className="group mb-6 flex min-h-[52px] w-full items-center gap-3 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2.5 transition-colors hover:border-emerald-300 dark:border-[#D2F0E1]/5 dark:bg-[#092D22] dark:hover:border-[#00FFA3]/30"
      aria-label={t("announcements.barAria")}
    >
      <img src={noticeIcon} alt="" className="h-9 w-9 shrink-0" />
      <div
        className="h-5 min-w-0 flex-1 overflow-hidden"
        aria-live="polite"
        aria-atomic="true"
      >
        {items.length === 1 ? (
          <p className="h-5 truncate text-sm font-medium leading-5 text-slate-900 dark:text-white">
            {items[0].title}
          </p>
        ) : (
          <div
            className={
              isTransitionEnabled
                ? "transition-transform duration-700 ease-in-out"
                : "transition-none"
            }
            style={{
              transform: `translateY(-${activeIndex * TICKER_LINE_HEIGHT_PX}px)`,
            }}
          >
            {tickerItems.map((item, index) => (
              <p
                key={`${item.id}-${index}`}
                className="h-5 truncate text-sm font-medium leading-5 text-slate-900 dark:text-white"
              >
                {item.title}
              </p>
            ))}
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 shrink-0 text-slate-400 group-hover:text-emerald-600 dark:group-hover:text-[#00FFA3] transition-colors" />
    </Link>
  );
}
